/**
 * Groupings Configuration Panel
 * Admin panel for configuring trade grouping labels, values, and entity mappings.
 * Three collapsible sections, each with its own Save/Reset.
 *
 * grouping2Mode:
 * - 'subgrouping': G2 values are children of G1. Mapping: Entity→G1→G2.
 * - 'parallel': G1 and G2 independent. Mapping: Entity with stacked G1/G2 sections.
 *
 * grouping3Mode (requires grouping2Active):
 * - 'parallel': G3 independent, mapped directly to entities (separate section below G2).
 * - 'subgrouping': G3 values nested inline under their G2 parent in the mapping view.
 *
 * Four mapping UI combinations:
 * 1. G2 parallel + G3 parallel  → Entity card + stacked G1/G2/G3 sections (right col)
 * 2. G2 parallel + G3 subgroup  → Entity card + G1 section + G2 section (G3 inline under each G2)
 * 3. G2 subgroup + G3 parallel  → Entity→G1→G2 tree + separate G3 section below tree
 * 4. G2 subgroup + G3 subgroup  → Entity→G1→G2 tree (G3 inline under each G2 in branch cells)
 */

import React, { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import { groupingsService } from '../../services/api/groupingsService';
import { clientService, TradingEntity } from '../../services/api/clientService';
import { GroupingValue, EntityMapping, Grouping2Mode, Grouping3Mode } from '../../types/groupings';
import AlertModal from '../common/AlertModal';
import './GroupingsConfigPanel.css';

interface LineData {
  x1: number; y1: number; x2: number; y2: number;
  color: string;
}

const GroupingsConfigPanel: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const clientId = user?.organization?.id || '';

  // Loading / saving
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    type: 'success' | 'error' | 'info' | 'warning';
    title: string;
    message: string;
  }>({ isOpen: false, type: 'info', title: '', message: '' });

  const showSuccess = (message: string) => {
    setAlertModal({ isOpen: true, type: 'success', title: t('common.success'), message });
  };

  const showError = (message: string) => {
    setAlertModal({ isOpen: true, type: 'error', title: t('common.error'), message });
  };

  const closeAlertModal = () => {
    setAlertModal((prev) => ({ ...prev, isOpen: false }));
  };

  // Config state
  const [grouping1Label, setGrouping1Label] = useState('');
  const [grouping2Label, setGrouping2Label] = useState('');
  const [grouping2Mode, setGrouping2Mode] = useState<Grouping2Mode>('parallel');
  const [grouping3Label, setGrouping3Label] = useState('');
  const [grouping3Mode, setGrouping3Mode] = useState<Grouping3Mode>('parallel');
  const [grouping1Values, setGrouping1Values] = useState<GroupingValue[]>([]);
  const [grouping2Values, setGrouping2Values] = useState<GroupingValue[]>([]);
  const [grouping3Values, setGrouping3Values] = useState<GroupingValue[]>([]);
  const [entityMappings, setEntityMappings] = useState<EntityMapping[]>([]);
  const [tradingEntities, setTradingEntities] = useState<TradingEntity[]>([]);

  // Per-section original state for granular reset
  const [origLabels, setOrigLabels] = useState({
    g1: '', g2: '', g3: '',
    mode: 'parallel' as Grouping2Mode,
    g3Mode: 'parallel' as Grouping3Mode,
  });
  const [origValues, setOrigValues] = useState({
    g1: [] as GroupingValue[],
    g2: [] as GroupingValue[],
    g3: [] as GroupingValue[],
  });
  const [origMappings, setOrigMappings] = useState<EntityMapping[]>([]);

  // Collapsible sections
  const [labelsExpanded, setLabelsExpanded] = useState(false);
  const [valuesExpanded, setValuesExpanded] = useState(false);
  const [mappingsExpanded, setMappingsExpanded] = useState(true);

  // Entity mapping selection
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);
  const [selectedG1ValueId, setSelectedG1ValueId] = useState<string | null>(null);

  // SVG line drawing
  const [lines, setLines] = useState<LineData[]>([]);
  const mappingContainerRef = useRef<HTMLDivElement>(null);
  const nodeRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const setNodeRef = useCallback((key: string) => (el: HTMLDivElement | null) => {
    if (el) {
      nodeRefs.current.set(key, el);
    } else {
      nodeRefs.current.delete(key);
    }
  }, []);

  // Derived
  const grouping2Active = grouping2Label.trim().length > 0;
  const grouping3Active = grouping2Active && grouping3Label.trim().length > 0;
  const activeG1Values = grouping1Values.filter(v => v.isActive && v.id);
  const activeG2Values = grouping2Values.filter(v => v.isActive && v.id);
  const activeG3Values = grouping3Values.filter(v => v.isActive && v.id);
  const isSubgrouping = grouping2Mode === 'subgrouping';
  const isG3Subgrouping = grouping3Mode === 'subgrouping';

  // Per-section change detection
  const hasLabelsChanges = grouping1Label !== origLabels.g1
    || grouping2Label !== origLabels.g2
    || grouping3Label !== origLabels.g3
    || grouping2Mode !== origLabels.mode
    || grouping3Mode !== origLabels.g3Mode;
  const hasValuesChanges = JSON.stringify(grouping1Values) !== JSON.stringify(origValues.g1)
    || JSON.stringify(grouping2Values) !== JSON.stringify(origValues.g2)
    || JSON.stringify(grouping3Values) !== JSON.stringify(origValues.g3);
  const hasMappingsChanges = JSON.stringify(entityMappings) !== JSON.stringify(origMappings);

  const sortedEntities = [...tradingEntities].sort((a, b) => {
    if (a.isParent && !b.isParent) return -1;
    if (!a.isParent && b.isParent) return 1;
    return a.name.localeCompare(b.name);
  });

  // ── Data loading ──

  useEffect(() => {
    if (clientId) loadData();
  }, [clientId]);

  const loadData = async () => {
    if (!clientId) return;
    setLoading(true);
    setError(null);
    try {
      const [config, entitiesResponse] = await Promise.all([
        groupingsService.getGroupings(clientId),
        clientService.getTradingEntities(clientId),
      ]);
      if (entitiesResponse.success && entitiesResponse.data) {
        setTradingEntities(entitiesResponse.data);
      }
      const g1Label = config?.grouping1Label || '';
      const g2Label = config?.grouping2Label || '';
      const g2Mode: Grouping2Mode = config?.grouping2Mode || 'parallel';
      const g3Label = config?.grouping3Label || '';
      const g3Mode: Grouping3Mode = config?.grouping3Mode || 'parallel';
      const g1Values = config?.grouping1Values || [];
      const g2Values = config?.grouping2Values || [];
      const g3Values = config?.grouping3Values || [];
      const mappings = config?.entityMappings || [];
      if (config) {
        setGrouping1Label(g1Label);
        setGrouping2Label(g2Label);
        setGrouping2Mode(g2Mode);
        setGrouping3Label(g3Label);
        setGrouping3Mode(g3Mode);
        setGrouping1Values(g1Values);
        setGrouping2Values(g2Values);
        setGrouping3Values(g3Values);
        setEntityMappings(mappings);
      }
      setOrigLabels({ g1: g1Label, g2: g2Label, g3: g3Label, mode: g2Mode, g3Mode: g3Mode });
      setOrigValues({
        g1: JSON.parse(JSON.stringify(g1Values)),
        g2: JSON.parse(JSON.stringify(g2Values)),
        g3: JSON.parse(JSON.stringify(g3Values)),
      });
      setOrigMappings(JSON.parse(JSON.stringify(mappings)));
    } catch (err: any) {
      setError(err.message || t('admin.groupings.loadError'));
    } finally {
      setLoading(false);
    }
  };

  // ── Save ──

  const handleSave = async () => {
    if (!clientId) return;
    if (!grouping1Label.trim()) {
      setError(t('admin.groupings.labelRequired'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const allMappings = ensureAllEntitiesMapped();
      const saved = await groupingsService.saveGroupings(clientId, {
        grouping1Label: grouping1Label.trim(),
        grouping2Label: grouping2Label.trim(),
        grouping2Mode,
        grouping3Label: grouping3Label.trim(),
        grouping3Mode,
        grouping1Values,
        grouping2Values,
        grouping3Values,
        entityMappings: allMappings,
      });
      const sG1V = saved.grouping1Values || [];
      const sG2V = saved.grouping2Values || [];
      const sG3V = saved.grouping3Values || [];
      const sM = saved.entityMappings || [];
      setGrouping1Values(sG1V);
      setGrouping2Values(sG2V);
      setGrouping3Values(sG3V);
      setEntityMappings(sM);
      setOrigLabels({
        g1: saved.grouping1Label || '',
        g2: saved.grouping2Label || '',
        g3: saved.grouping3Label || '',
        mode: saved.grouping2Mode || 'parallel',
        g3Mode: saved.grouping3Mode || 'parallel',
      });
      setOrigValues({
        g1: JSON.parse(JSON.stringify(sG1V)),
        g2: JSON.parse(JSON.stringify(sG2V)),
        g3: JSON.parse(JSON.stringify(sG3V)),
      });
      setOrigMappings(JSON.parse(JSON.stringify(sM)));
      showSuccess(t('admin.groupings.saveSuccess'));
    } catch (err: any) {
      showError(err.message || t('admin.groupings.saveError'));
    } finally {
      setSaving(false);
    }
  };

  // ── Per-section reset ──

  const resetLabels = () => {
    setGrouping1Label(origLabels.g1);
    setGrouping2Label(origLabels.g2);
    setGrouping3Label(origLabels.g3);
    setGrouping2Mode(origLabels.mode);
    setGrouping3Mode(origLabels.g3Mode);
    setError(null);
  };

  const resetValues = () => {
    setGrouping1Values(JSON.parse(JSON.stringify(origValues.g1)));
    setGrouping2Values(JSON.parse(JSON.stringify(origValues.g2)));
    setGrouping3Values(JSON.parse(JSON.stringify(origValues.g3)));
    setError(null);
  };

  const resetMappings = () => {
    setEntityMappings(JSON.parse(JSON.stringify(origMappings)));
    setSelectedEntityId(null);
    setSelectedG1ValueId(null);
    setError(null);
  };

  const ensureAllEntitiesMapped = (): EntityMapping[] => {
    const mappings = [...entityMappings];
    for (const entity of tradingEntities) {
      if (!mappings.find(m => m.entityId === entity.id)) {
        mappings.push({ entityId: entity.id, grouping1ValueIds: [], grouping2ValueIds: [], g1ToG2Mappings: {}, grouping3ValueIds: [], g2ToG3Mappings: {} });
      }
    }
    return mappings;
  };

  // ── Value management ──

  const addValue = (g: 1 | 2 | 3) => {
    const v: GroupingValue = { id: '', name: '', isActive: true };
    if (g === 1) setGrouping1Values([...grouping1Values, v]);
    else if (g === 2) setGrouping2Values([...grouping2Values, v]);
    else setGrouping3Values([...grouping3Values, v]);
  };

  const updateValueName = (g: 1 | 2 | 3, idx: number, name: string) => {
    if (g === 1) {
      const updated = [...grouping1Values];
      updated[idx] = { ...updated[idx], name };
      setGrouping1Values(updated);
    } else if (g === 2) {
      const updated = [...grouping2Values];
      updated[idx] = { ...updated[idx], name };
      setGrouping2Values(updated);
    } else {
      const updated = [...grouping3Values];
      updated[idx] = { ...updated[idx], name };
      setGrouping3Values(updated);
    }
  };

  const toggleValueActive = (g: 1 | 2 | 3, idx: number) => {
    if (g === 1) {
      const updated = [...grouping1Values];
      updated[idx] = { ...updated[idx], isActive: !updated[idx].isActive };
      setGrouping1Values(updated);
    } else if (g === 2) {
      const updated = [...grouping2Values];
      updated[idx] = { ...updated[idx], isActive: !updated[idx].isActive };
      setGrouping2Values(updated);
    } else {
      const updated = [...grouping3Values];
      updated[idx] = { ...updated[idx], isActive: !updated[idx].isActive };
      setGrouping3Values(updated);
    }
  };

  // ── Entity mapping management ──

  const getEntityMapping = (entityId: string): EntityMapping => {
    const found = entityMappings.find(m => m.entityId === entityId);
    if (!found) return { entityId, grouping1ValueIds: [], grouping2ValueIds: [], g1ToG2Mappings: {}, grouping3ValueIds: [], g2ToG3Mappings: {} };
    return { ...found, g1ToG2Mappings: found.g1ToG2Mappings || {}, g2ToG3Mappings: found.g2ToG3Mappings || {} };
  };

  const toggleEntityGroupingValue = (entityId: string, g: 1 | 2 | 3, valueId: string) => {
    const updatedMappings = [...entityMappings];
    let mapping = updatedMappings.find(m => m.entityId === entityId);
    if (!mapping) {
      mapping = { entityId, grouping1ValueIds: [], grouping2ValueIds: [], g1ToG2Mappings: {}, grouping3ValueIds: [], g2ToG3Mappings: {} };
      updatedMappings.push(mapping);
    }
    const key = g === 1 ? 'grouping1ValueIds' : g === 2 ? 'grouping2ValueIds' : 'grouping3ValueIds';
    const ids = mapping[key];
    const idx = ids.indexOf(valueId);
    mapping[key] = idx >= 0 ? ids.filter(id => id !== valueId) : [...ids, valueId];
    setEntityMappings(updatedMappings);
  };

  const toggleG1ToG2Mapping = (entityId: string, g1Id: string, g2Id: string) => {
    const updatedMappings = [...entityMappings];
    let mapping = updatedMappings.find(m => m.entityId === entityId);
    if (!mapping) {
      mapping = { entityId, grouping1ValueIds: [], grouping2ValueIds: [], g1ToG2Mappings: {}, grouping3ValueIds: [], g2ToG3Mappings: {} };
      updatedMappings.push(mapping);
    }
    const g2Ids = mapping.g1ToG2Mappings[g1Id] || [];
    const exists = g2Ids.includes(g2Id);
    mapping.g1ToG2Mappings = {
      ...mapping.g1ToG2Mappings,
      [g1Id]: exists ? g2Ids.filter(id => id !== g2Id) : [...g2Ids, g2Id],
    };
    setEntityMappings(updatedMappings);
  };

  const toggleG2ToG3Mapping = (entityId: string, g2Id: string, g3Id: string) => {
    const updatedMappings = [...entityMappings];
    let mapping = updatedMappings.find(m => m.entityId === entityId);
    if (!mapping) {
      mapping = { entityId, grouping1ValueIds: [], grouping2ValueIds: [], g1ToG2Mappings: {}, grouping3ValueIds: [], g2ToG3Mappings: {} };
      updatedMappings.push(mapping);
    }
    const g3Ids = mapping.g2ToG3Mappings[g2Id] || [];
    const exists = g3Ids.includes(g3Id);
    mapping.g2ToG3Mappings = {
      ...mapping.g2ToG3Mappings,
      [g2Id]: exists ? g3Ids.filter(id => id !== g3Id) : [...g3Ids, g3Id],
    };
    setEntityMappings(updatedMappings);
  };

  // ── SVG line computation ──
  // G3 subgrouping is inline under G2 (no SVG lines needed — visually adjacent).
  // G3 parallel draws entity→G3 lines (green) in both G2 modes.

  const computeLines = useCallback(() => {
    if (!mappingContainerRef.current) { setLines([]); return; }
    const container = mappingContainerRef.current;
    const cRect = container.getBoundingClientRect();
    const newLines: LineData[] = [];

    if (isSubgrouping) {
      // Entity→G1 lines (when entity selected)
      if (selectedEntityId) {
        const entityEl = nodeRefs.current.get(`entity-${selectedEntityId}`);
        if (entityEl) {
          const eRect = entityEl.getBoundingClientRect();
          const eY = eRect.top + eRect.height / 2 - cRect.top;
          const eX = eRect.right - cRect.left;
          const mapping = getEntityMapping(selectedEntityId);
          for (const vId of mapping.grouping1ValueIds) {
            const el = nodeRefs.current.get(`g1-${vId}`);
            if (el) {
              const r = el.getBoundingClientRect();
              newLines.push({
                x1: eX, y1: eY,
                x2: r.left - cRect.left, y2: r.top + r.height / 2 - cRect.top,
                color: 'var(--accent-blue)',
              });
            }
          }
          // G3 parallel: entity→G3 lines (green)
          if (grouping3Active && !isG3Subgrouping) {
            for (const vId of mapping.grouping3ValueIds) {
              const el = nodeRefs.current.get(`g3-${vId}`);
              if (el) {
                const r = el.getBoundingClientRect();
                newLines.push({
                  x1: eX, y1: eY,
                  x2: r.left - cRect.left, y2: r.top + r.height / 2 - cRect.top,
                  color: 'var(--accent-green, #10b981)',
                });
              }
            }
          }
        }
      }
      // G1→G2 lines (for all connected G1s of the selected entity)
      if (selectedEntityId) {
        const mapping = getEntityMapping(selectedEntityId);
        for (const g1Id of mapping.grouping1ValueIds) {
          const g1El = nodeRefs.current.get(`g1-${g1Id}`);
          if (!g1El) continue;
          const g1Rect = g1El.getBoundingClientRect();
          const g1Y = g1Rect.top + g1Rect.height / 2 - cRect.top;
          const g1X = g1Rect.right - cRect.left;
          for (const g2Id of mapping.g1ToG2Mappings[g1Id] || []) {
            const el = nodeRefs.current.get(`g2-${g1Id}-${g2Id}`);
            if (el) {
              const r = el.getBoundingClientRect();
              newLines.push({
                x1: g1X, y1: g1Y,
                x2: r.left - cRect.left, y2: r.top + r.height / 2 - cRect.top,
                color: 'var(--accent-purple, #a855f7)',
              });
            }
          }
        }
      }
    } else {
      // Parallel mode: entity→G1, entity→G2, entity→G3 lines
      if (selectedEntityId) {
        const entityEl = nodeRefs.current.get(`entity-${selectedEntityId}`);
        if (entityEl) {
          const eRect = entityEl.getBoundingClientRect();
          const eY = eRect.top + eRect.height / 2 - cRect.top;
          const eX = eRect.right - cRect.left;
          const mapping = getEntityMapping(selectedEntityId);
          for (const vId of mapping.grouping1ValueIds) {
            const el = nodeRefs.current.get(`g1-${vId}`);
            if (el) {
              const r = el.getBoundingClientRect();
              newLines.push({
                x1: eX, y1: eY,
                x2: r.left - cRect.left, y2: r.top + r.height / 2 - cRect.top,
                color: 'var(--accent-blue)',
              });
            }
          }
          for (const vId of mapping.grouping2ValueIds) {
            const el = nodeRefs.current.get(`g2-${vId}`);
            if (el) {
              const r = el.getBoundingClientRect();
              newLines.push({
                x1: eX, y1: eY,
                x2: r.left - cRect.left, y2: r.top + r.height / 2 - cRect.top,
                color: 'var(--accent-purple, #a855f7)',
              });
            }
          }
          // G3 parallel: entity→G3 lines (green)
          if (grouping3Active && !isG3Subgrouping) {
            for (const vId of mapping.grouping3ValueIds) {
              const el = nodeRefs.current.get(`g3-${vId}`);
              if (el) {
                const r = el.getBoundingClientRect();
                newLines.push({
                  x1: eX, y1: eY,
                  x2: r.left - cRect.left, y2: r.top + r.height / 2 - cRect.top,
                  color: 'var(--accent-green, #10b981)',
                });
              }
            }
          }
        }
      }
    }

    setLines(newLines);
  }, [selectedEntityId, selectedG1ValueId, entityMappings, activeG1Values, activeG2Values, activeG3Values, isSubgrouping, grouping3Active, isG3Subgrouping]);

  useLayoutEffect(() => {
    requestAnimationFrame(computeLines);
  }, [computeLines]);

  useEffect(() => {
    const onResize = () => computeLines();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [computeLines]);

  // ── Section action buttons ──

  const renderSectionActions = (sectionHasChanges: boolean, onReset: () => void) => (
    <div className="section-actions">
      <button className="reset-button" onClick={onReset} disabled={!sectionHasChanges || saving}>
        {t('common.reset')}
      </button>
      <button className="save-button" onClick={handleSave} disabled={!sectionHasChanges || saving || !grouping1Label.trim()}>
        {saving ? t('common.saving') : t('common.save')}
      </button>
    </div>
  );

  // ── Values table ──

  const renderValuesTable = (g: 1 | 2 | 3) => {
    const values = g === 1 ? grouping1Values : g === 2 ? grouping2Values : grouping3Values;
    const label = g === 1 ? grouping1Label : g === 2 ? grouping2Label : grouping3Label;
    const fallback = g === 1
      ? t('admin.groupings.grouping1')
      : g === 2
      ? t('admin.groupings.grouping2')
      : t('admin.groupings.grouping3');

    return (
      <div className="values-section">
        <div className="values-header">
          <span className="values-label">{label || fallback}:</span>
          <button className="add-value-btn" onClick={() => addValue(g)}>
            + {t('admin.groupings.addValue')}
          </button>
        </div>
        {values.length === 0 ? (
          <p className="no-values-text">{t('admin.groupings.noValues')}</p>
        ) : (
          <table className="values-table">
            <thead>
              <tr>
                <th>{t('admin.groupings.valueName')}</th>
                <th style={{ width: '30%' }}>{t('admin.groupings.active')}</th>
              </tr>
            </thead>
            <tbody>
              {values.map((value, idx) => (
                <tr key={value.id || `new-${idx}`} className={!value.isActive ? 'inactive-row' : ''}>
                  <td>
                    <input
                      className="value-name-input"
                      type="text"
                      value={value.name}
                      onChange={(e) => updateValueName(g, idx, e.target.value)}
                      placeholder={t('admin.groupings.valueName')}
                    />
                  </td>
                  <td>
                    <button
                      className={`toggle-btn ${value.isActive ? 'active' : 'inactive'}`}
                      onClick={() => toggleValueActive(g, idx)}
                    >
                      {value.isActive ? t('admin.groupings.active') : t('admin.groupings.inactive')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  };

  // ── Mapping click handlers (G1/G2 in subgrouping mode) ──

  const handleValueNodeClick = (g: 1 | 2, valueId: string) => {
    if (isSubgrouping && g === 1) {
      // In subgrouping mode, clicking G1 toggles entity→G1 and selects G1 to show its G2s
      setSelectedG1ValueId(selectedG1ValueId === valueId ? null : valueId);
      if (selectedEntityId) {
        toggleEntityGroupingValue(selectedEntityId, 1, valueId);
      }
      return;
    }
    if (isSubgrouping && g === 2) {
      // In subgrouping mode, clicking G2 toggles G1→G2 link for the selected entity's G1
      if (!selectedG1ValueId || !selectedEntityId) return;
      toggleG1ToG2Mapping(selectedEntityId, selectedG1ValueId, valueId);
      return;
    }
    if (!selectedEntityId) return;
    toggleEntityGroupingValue(selectedEntityId, g, valueId);
  };

  const isValueConnected = (g: 1 | 2 | 3, valueId: string): boolean => {
    if (!selectedEntityId) return false;
    const mapping = getEntityMapping(selectedEntityId);
    const ids = g === 1 ? mapping.grouping1ValueIds : g === 2 ? mapping.grouping2ValueIds : mapping.grouping3ValueIds;
    return ids.includes(valueId);
  };

  // ── SVG lines helper ──

  const renderSvgLines = () => (
    <svg className="mapping-lines-svg">
      {lines.map((line, i) => {
        const dx = line.x2 - line.x1;
        const cp = dx * 0.4;
        return (
          <path
            key={i}
            d={`M ${line.x1} ${line.y1} C ${line.x1 + cp} ${line.y1}, ${line.x2 - cp} ${line.y2}, ${line.x2} ${line.y2}`}
            stroke={line.color}
            strokeWidth="2"
            fill="none"
            opacity="0.7"
          />
        );
      })}
    </svg>
  );

  // ── G3 inline sub-section (G3 subgrouping: nested under a G2 node) ──

  const renderG3InlineSection = (entityId: string, g2Id: string) => {
    const mapping = getEntityMapping(entityId);
    const connectedG3Ids = mapping.g2ToG3Mappings[g2Id] || [];
    const connectedG3 = activeG3Values.filter(v => connectedG3Ids.includes(v.id));
    const unconnectedG3 = activeG3Values.filter(v => !connectedG3Ids.includes(v.id));
    const g3LabelText = grouping3Label || t('admin.groupings.grouping3');

    return (
      <div className="g3-inline-section">
        <div className="g3-inline-label">{g3LabelText}</div>
        {connectedG3.map(g3Value => (
          <div
            key={g3Value.id}
            className="mapping-node value-node clickable connected g3-connected"
            onClick={() => toggleG2ToG3Mapping(entityId, g2Id, g3Value.id)}
          >
            <span className="node-label">{g3Value.name}</span>
            <span className="node-remove">&times;</span>
          </div>
        ))}
        {unconnectedG3.length > 0 && (
          <select
            className="col-add-select"
            value=""
            onChange={(e) => {
              if (e.target.value) toggleG2ToG3Mapping(entityId, g2Id, e.target.value);
            }}
          >
            <option value="" disabled>+ {t('admin.groupings.addValue')}...</option>
            {unconnectedG3.map(v => (
              <option key={v.id} value={v.id}>{v.name}</option>
            ))}
          </select>
        )}
      </div>
    );
  };

  // ── G3 parallel section (G3 parallel: independent entity→G3 mapping) ──

  const renderG3ParallelSection = (entityId: string | null) => {
    const g3LabelText = grouping3Label || t('admin.groupings.grouping3');
    const mapping = entityId ? getEntityMapping(entityId) : null;
    const connectedG3 = mapping ? activeG3Values.filter(v => mapping.grouping3ValueIds.includes(v.id)) : [];
    const unconnectedG3 = mapping ? activeG3Values.filter(v => !mapping.grouping3ValueIds.includes(v.id)) : [];

    return (
      <div className="parallel-mapping-section">
        <div className="mapping-col-header">{g3LabelText}</div>
        {connectedG3.map(value => (
          <div
            key={value.id}
            ref={setNodeRef(`g3-${value.id}`)}
            className="mapping-node value-node clickable connected g3-connected"
            onClick={() => entityId && toggleEntityGroupingValue(entityId, 3, value.id)}
          >
            <span className="node-label">{value.name}</span>
            <span className="node-remove">&times;</span>
          </div>
        ))}
        {entityId && unconnectedG3.length > 0 && (
          <select
            className="col-add-select"
            value=""
            onChange={(e) => {
              if (e.target.value) toggleEntityGroupingValue(entityId, 3, e.target.value);
            }}
          >
            <option value="" disabled>+ {t('admin.groupings.addValue')}...</option>
            {unconnectedG3.map(v => (
              <option key={v.id} value={v.id}>{v.name}</option>
            ))}
          </select>
        )}
      </div>
    );
  };

  // ── Subgrouping mapping view: Entity dropdown → G1 → G2 [→ G3 inline or G3 section] ──

  const renderSubgroupingMappingView = () => {
    const hasG1 = activeG1Values.length > 0;
    const hasG2 = grouping2Active && activeG2Values.length > 0;

    if (sortedEntities.length === 0) {
      return <p className="no-values-text">{t('admin.groupings.noEntities')}</p>;
    }
    if (!hasG1) {
      return <p className="no-values-text">{t('admin.groupings.noValuesForMapping')}</p>;
    }

    const selectedEntity = sortedEntities.find(e => e.id === selectedEntityId);
    const columnCount = 1 + 1 + (hasG2 ? 1 : 0);

    const mapping = selectedEntityId ? getEntityMapping(selectedEntityId) : null;
    const connectedG1 = mapping
      ? activeG1Values.filter(v => mapping.grouping1ValueIds.includes(v.id))
      : [];
    const unconnectedG1 = mapping
      ? activeG1Values.filter(v => !mapping.grouping1ValueIds.includes(v.id))
      : [];

    const g1Label = grouping1Label || t('admin.groupings.grouping1');
    const g2Label = grouping2Label || t('admin.groupings.grouping2');

    return (
      <div className="mapping-view" ref={mappingContainerRef} style={{ position: 'relative' }}>
        {renderSvgLines()}

        <div className="mapping-columns mapping-tree-grid" style={{ gridTemplateColumns: `repeat(${columnCount}, 1fr)` }}>
          {/* Row 1: Entity selector dropdown */}
          <select
            className="col-add-select"
            value={selectedEntityId || ''}
            onChange={(e) => {
              setSelectedEntityId(e.target.value || null);
              setSelectedG1ValueId(null);
            }}
          >
            <option value="">{t('admin.groupings.selectEntity')}</option>
            {sortedEntities.map(entity => (
              <option key={entity.id} value={entity.id}>
                {entity.isParent ? entity.name : `\u2514\u2500 ${entity.name}`}
              </option>
            ))}
          </select>
          <div className="col-add-placeholder" />
          {hasG2 && <div className="col-add-placeholder" />}

          {/* Row 2: Column headers */}
          <div className="mapping-col-header">{t('admin.groupings.entity')}</div>
          <div className="mapping-col-header">{g1Label}</div>
          {hasG2 && <div className="mapping-col-header">{g2Label}</div>}

          {/* Rows 3+: Entity card spanning all branch rows + G1/G2 branches */}
          {selectedEntityId && selectedEntity && (
            <>
              <div style={{ gridColumn: 1, gridRow: `3 / span ${Math.max(connectedG1.length + (unconnectedG1.length > 0 ? 1 : 0), 1)}`, alignSelf: 'start' }}>
                <div
                  ref={setNodeRef(`entity-${selectedEntityId}`)}
                  className="mapping-node entity-node selected"
                >
                  {selectedEntity.name}
                </div>
              </div>

              {connectedG1.map(g1Value => {
                const g2Ids = mapping!.g1ToG2Mappings[g1Value.id] || [];
                const branchG2 = hasG2 ? activeG2Values.filter(v => g2Ids.includes(v.id)) : [];
                const branchUnconnectedG2 = hasG2 ? activeG2Values.filter(v => !g2Ids.includes(v.id)) : [];

                return (
                  <React.Fragment key={g1Value.id}>
                    <div style={{ gridColumn: 2, alignSelf: 'start' }}>
                      <div
                        ref={setNodeRef(`g1-${g1Value.id}`)}
                        className="mapping-node value-node clickable connected"
                        onClick={() => toggleEntityGroupingValue(selectedEntityId!, 1, g1Value.id)}
                      >
                        <span className="node-label">{g1Value.name}</span>
                        <span className="node-remove">&times;</span>
                      </div>
                    </div>
                    {hasG2 && (
                      <div style={{ gridColumn: 3 }} className="g2-branch-cell">
                        {branchG2.map(g2Value => (
                          <React.Fragment key={g2Value.id}>
                            <div
                              ref={setNodeRef(`g2-${g1Value.id}-${g2Value.id}`)}
                              className="mapping-node value-node clickable connected g2-connected"
                              onClick={() => toggleG1ToG2Mapping(selectedEntityId!, g1Value.id, g2Value.id)}
                            >
                              <span className="node-label">{g2Value.name}</span>
                              <span className="node-remove">&times;</span>
                            </div>
                            {grouping3Active && isG3Subgrouping && (
                              renderG3InlineSection(selectedEntityId!, g2Value.id)
                            )}
                          </React.Fragment>
                        ))}
                        {branchUnconnectedG2.length > 0 && (
                          <select
                            className="col-add-select"
                            value=""
                            onChange={(e) => {
                              if (e.target.value) toggleG1ToG2Mapping(selectedEntityId!, g1Value.id, e.target.value);
                            }}
                          >
                            <option value="" disabled>+ {t('admin.groupings.addValue')}...</option>
                            {branchUnconnectedG2.map(v => (
                              <option key={v.id} value={v.id}>{v.name}</option>
                            ))}
                          </select>
                        )}
                      </div>
                    )}
                  </React.Fragment>
                );
              })}

              {/* G1 add dropdown at bottom of G1 column */}
              {unconnectedG1.length > 0 && (
                <div style={{ gridColumn: 2 }}>
                  <select
                    className="col-add-select"
                    value=""
                    onChange={(e) => {
                      if (e.target.value) toggleEntityGroupingValue(selectedEntityId!, 1, e.target.value);
                    }}
                  >
                    <option value="" disabled>+ {t('admin.groupings.addValue')}...</option>
                    {unconnectedG1.map(v => (
                      <option key={v.id} value={v.id}>{v.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* G3 parallel: in G1 column below the tree (mode: G2 subgrouping + G3 parallel) */}
              {grouping3Active && !isG3Subgrouping && (
                <div style={{ gridColumn: 2, marginTop: '1.5rem' }}>
                  {renderG3ParallelSection(selectedEntityId)}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  // ── Parallel mapping view: Entity dropdown → G1 + G2 + G3 ──

  const renderParallelMappingView = () => {
    const hasG1 = activeG1Values.length > 0;
    const hasG2 = grouping2Active && activeG2Values.length > 0;

    if (sortedEntities.length === 0) {
      return <p className="no-values-text">{t('admin.groupings.noEntities')}</p>;
    }
    if (!hasG1 && !hasG2) {
      return <p className="no-values-text">{t('admin.groupings.noValuesForMapping')}</p>;
    }

    const selectedEntity = sortedEntities.find(e => e.id === selectedEntityId);

    const mapping = selectedEntityId ? getEntityMapping(selectedEntityId) : null;
    const connectedG1 = mapping
      ? activeG1Values.filter(v => mapping.grouping1ValueIds.includes(v.id))
      : [];
    const unconnectedG1 = mapping
      ? activeG1Values.filter(v => !mapping.grouping1ValueIds.includes(v.id))
      : [];
    const connectedG2 = mapping
      ? activeG2Values.filter(v => mapping.grouping2ValueIds.includes(v.id))
      : [];
    const unconnectedG2 = mapping
      ? activeG2Values.filter(v => !mapping.grouping2ValueIds.includes(v.id))
      : [];

    const g1Label = grouping1Label || t('admin.groupings.grouping1');
    const g2Label = grouping2Label || t('admin.groupings.grouping2');

    return (
      <div className="mapping-view" ref={mappingContainerRef} style={{ position: 'relative' }}>
        {renderSvgLines()}

        {/* Entity selector - full width */}
        <select
          className="col-add-select"
          value={selectedEntityId || ''}
          onChange={(e) => {
            setSelectedEntityId(e.target.value || null);
            setSelectedG1ValueId(null);
          }}
          style={{ marginBottom: '0.75rem' }}
        >
          <option value="">{t('admin.groupings.selectEntity')}</option>
          {sortedEntities.map(entity => (
            <option key={entity.id} value={entity.id}>
              {entity.isParent ? entity.name : `\u2514\u2500 ${entity.name}`}
            </option>
          ))}
        </select>

        <div className="mapping-columns" style={{ gridTemplateColumns: '1fr 1fr' }}>
          {/* Left column: Entity card */}
          <div className="mapping-col">
            <div className="mapping-col-header">{t('admin.groupings.entity')}</div>
            {selectedEntityId && selectedEntity && (
              <div
                ref={setNodeRef(`entity-${selectedEntityId}`)}
                className="mapping-node entity-node selected"
              >
                {selectedEntity.name}
              </div>
            )}
          </div>

          {/* Right column: G1, G2, G3 sections stacked */}
          <div className="mapping-col">
            {/* G1 section */}
            {hasG1 && (
              <div className="parallel-mapping-section">
                <div className="mapping-col-header">{g1Label}</div>
                {connectedG1.map(value => (
                  <div
                    key={value.id}
                    ref={setNodeRef(`g1-${value.id}`)}
                    className="mapping-node value-node clickable connected g1-connected"
                    onClick={() => toggleEntityGroupingValue(selectedEntityId!, 1, value.id)}
                  >
                    <span className="node-label">{value.name}</span>
                    <span className="node-remove">&times;</span>
                  </div>
                ))}
                {selectedEntityId && unconnectedG1.length > 0 && (
                  <select
                    className="col-add-select"
                    value=""
                    onChange={(e) => {
                      if (e.target.value) toggleEntityGroupingValue(selectedEntityId, 1, e.target.value);
                    }}
                  >
                    <option value="" disabled>+ {t('admin.groupings.addValue')}...</option>
                    {unconnectedG1.map(v => (
                      <option key={v.id} value={v.id}>{v.name}</option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {/* G2 section (mode 1: parallel+parallel; mode 2: parallel+subgrouping) */}
            {hasG2 && (
              <div className="parallel-mapping-section">
                <div className="mapping-col-header">{g2Label}</div>
                {connectedG2.map(value => (
                  <React.Fragment key={value.id}>
                    <div
                      ref={setNodeRef(`g2-${value.id}`)}
                      className="mapping-node value-node clickable connected g2-connected"
                      onClick={() => toggleEntityGroupingValue(selectedEntityId!, 2, value.id)}
                    >
                      <span className="node-label">{value.name}</span>
                      <span className="node-remove">&times;</span>
                    </div>
                    {/* Mode 2: G3 subgrouping — G3 inline under each G2 */}
                    {grouping3Active && isG3Subgrouping && selectedEntityId && (
                      renderG3InlineSection(selectedEntityId, value.id)
                    )}
                  </React.Fragment>
                ))}
                {selectedEntityId && unconnectedG2.length > 0 && (
                  <select
                    className="col-add-select"
                    value=""
                    onChange={(e) => {
                      if (e.target.value) toggleEntityGroupingValue(selectedEntityId, 2, e.target.value);
                    }}
                  >
                    <option value="" disabled>+ {t('admin.groupings.addValue')}...</option>
                    {unconnectedG2.map(v => (
                      <option key={v.id} value={v.id}>{v.name}</option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {/* Mode 1: G3 parallel — separate G3 section */}
            {grouping3Active && !isG3Subgrouping && (
              renderG3ParallelSection(selectedEntityId)
            )}
          </div>
        </div>
      </div>
    );
  };

  // ── Render ──

  if (loading) {
    return (
      <div className="groupings-config-panel loading">
        <div className="spinner" />
        <span>{t('common.loading')}</span>
      </div>
    );
  }

  return (
    <div className="groupings-config-panel">
      <div className="panel-header">
        <h3>{t('admin.groupings.title')}</h3>
      </div>

      {error && <div className="message-banner error"><p>{error}</p></div>}

      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={closeAlertModal}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
      />

      {/* ── Section 1: Labels ── */}
      <div className="collapsible-section">
        <div className="section-header" onClick={() => setLabelsExpanded(!labelsExpanded)}>
          <span className={`expand-icon ${labelsExpanded ? 'expanded' : ''}`}>▶</span>
          <h4>{t('admin.groupings.labelsTitle')}</h4>
        </div>
        {labelsExpanded && (
          <div className="section-content">
            <div className="labels-compact">
              <div className="label-field">
                <label>{t('admin.groupings.grouping1Label')}</label>
                <input
                  type="text" value={grouping1Label}
                  onChange={(e) => setGrouping1Label(e.target.value)}
                  placeholder={t('admin.groupings.grouping1Placeholder')}
                  maxLength={50}
                />
              </div>
              <div className="label-field">
                <label>{t('admin.groupings.grouping2Label')}</label>
                <div className="g2-input-row">
                  <input
                    type="text" value={grouping2Label}
                    onChange={(e) => setGrouping2Label(e.target.value)}
                    placeholder={t('admin.groupings.grouping2Placeholder')}
                    maxLength={50}
                  />
                  {grouping2Active && (
                    <label className="mode-checkbox-inline">
                      <input
                        type="checkbox"
                        checked={isSubgrouping}
                        onChange={(e) => setGrouping2Mode(e.target.checked ? 'subgrouping' : 'parallel')}
                      />
                      <span>{t('admin.groupings.g2IsSubgrouping').replace(
                        t('admin.groupings.grouping1'),
                        grouping1Label || t('admin.groupings.grouping1')
                      )}</span>
                    </label>
                  )}
                </div>
              </div>
              {grouping2Active && (
                <div className="label-field">
                  <label>{t('admin.groupings.grouping3Label')}</label>
                  <div className="g2-input-row">
                    <input
                      type="text" value={grouping3Label}
                      onChange={(e) => setGrouping3Label(e.target.value)}
                      placeholder={t('admin.groupings.grouping3Placeholder')}
                      maxLength={50}
                    />
                    {grouping3Active && (
                      <label className="mode-checkbox-inline">
                        <input
                          type="checkbox"
                          checked={isG3Subgrouping}
                          onChange={(e) => setGrouping3Mode(e.target.checked ? 'subgrouping' : 'parallel')}
                        />
                        <span>{t('admin.groupings.g3IsSubgrouping').replace(
                          t('admin.groupings.grouping2'),
                          grouping2Label || t('admin.groupings.grouping2')
                        )}</span>
                      </label>
                    )}
                  </div>
                </div>
              )}
            </div>
            {renderSectionActions(hasLabelsChanges, resetLabels)}
          </div>
        )}
      </div>

      {/* ── Section 2: Values ── */}
      <div className="collapsible-section">
        <div className="section-header" onClick={() => setValuesExpanded(!valuesExpanded)}>
          <span className={`expand-icon ${valuesExpanded ? 'expanded' : ''}`}>▶</span>
          <h4>{t('admin.groupings.valuesTitle')}</h4>
        </div>
        {valuesExpanded && (
          <div className="section-content">
            <div className="values-columns">
              <div className="values-column">
                {renderValuesTable(1)}
              </div>
              {grouping2Active && (
                <div className="values-column">
                  {renderValuesTable(2)}
                </div>
              )}
              {grouping3Active && (
                <div className="values-column">
                  {renderValuesTable(3)}
                </div>
              )}
            </div>
            {renderSectionActions(hasValuesChanges, resetValues)}
          </div>
        )}
      </div>

      {/* ── Section 3: Entity Mappings ── */}
      <div className="collapsible-section">
        <div className="section-header" onClick={() => setMappingsExpanded(!mappingsExpanded)}>
          <span className={`expand-icon ${mappingsExpanded ? 'expanded' : ''}`}>▶</span>
          <h4>{t('admin.groupings.entityMappings')}</h4>
        </div>
        {mappingsExpanded && (
          <div className="section-content">
            {isSubgrouping ? renderSubgroupingMappingView() : renderParallelMappingView()}
            {renderSectionActions(hasMappingsChanges, resetMappings)}
          </div>
        )}
      </div>
    </div>
  );
};

export default GroupingsConfigPanel;
